<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <h3 class="text-dark">
          <i class="el-icon-document-copy"></i>
          <span>Context</span>
        </h3>

        <p>
          <small>
            Configure what information your agent has access to during
            conversations. Context settings help your agent provide more
            personalized and relevant responses by including relevant data like
            contact details, communication history, and timestamps.
          </small>
        </p>

        <div class="context-list">
          <!-- Contexts with configurations -->
          <div
            v-for="context in contextsWithConfigs"
            :key="context.key"
            class="context-item context-item-full"
            data-testid="context-item"
          >
            <div class="context-header">
              <div class="context-toggle">
                <el-switch
                  v-model="context.enabled"
                  active-color="#00BF50"
                  :disabled="!isContextAvailable(context)"
                  @change="handleContextToggle(context)"
                  :data-testid="`context-toggle-${context.key}`"
                />
              </div>
              <div class="context-info">
                <h5 class="context-name">{{ context.label }}</h5>
                <p class="context-description">{{ context.description }}</p>
              </div>
            </div>

            <div v-if="context.enabled" class="context-config">
              <div
                v-for="config in context.config"
                :key="config.key"
                class="config-field"
              >
                <div class="form-label">
                  <h6 class="text-dark">{{ config.label }}</h6>
                  <small>{{ config.description }}</small>
                </div>

                <el-input-number
                  v-if="config.type === 'number'"
                  v-model="config.value"
                  :min="config.min || 1"
                  :max="config.max || 10"
                  :precision="0"
                  :controls="true"
                  class="w-100"
                  @change="handleConfigChange(context, config)"
                  :data-testid="`context-config-${context.key}-${config.key}`"
                />

                <el-input
                  v-else-if="config.type === 'text'"
                  v-model="config.value"
                  class="w-100"
                  @input="handleConfigChange(context, config)"
                  :data-testid="`context-config-${context.key}-${config.key}`"
                />

                <el-select
                  v-else-if="config.type === 'select'"
                  :value="config.value"
                  class="w-100"
                  :multiple="!!config.multiple"
                  @change="handleSelectChange(context, config, $event)"
                  @remove-tag="handleSelectChange(context, config, $event)"
                  :data-testid="`context-config-${context.key}-${config.key}`"
                >
                  <el-option
                    v-for="option in config.options"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value"
                  />
                </el-select>
              </div>
            </div>
          </div>

          <!-- Contexts without configurations -->
          <div
            v-for="context in contextsWithoutConfigs"
            :key="context.key"
            class="context-item context-item-simple"
            data-testid="context-item"
          >
            <div class="context-header">
              <div class="context-toggle">
                <el-switch
                  v-model="context.enabled"
                  active-color="#00BF50"
                  :disabled="!isContextAvailable(context)"
                  @change="handleContextToggle(context)"
                  :data-testid="`context-toggle-${context.key}`"
                />
              </div>
              <div class="context-info">
                <h5 class="context-name">{{ context.label }}</h5>
                <p class="context-description">{{ context.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { appInProduction } from '../../../../utils/environment-helpers'
export default {
  name: 'context-tab',

  props: {
    bot: {
      required: true,
      type: Object
    }
  },

  data() {
    return {
      availableContexts: [
        {
          key: 'contact_information',
          label: 'Contact Information',
          description:
            'Adds contact details like name, email, phone, and address',
          enabled: false,
          defaultEnabled: true
        },
        {
          key: 'communication_history',
          label: 'Communications',
          description:
            'Include communication history when the agent is talking to the contact',
          enabled: false,
          defaultEnabled: true
        },
        {
          key: 'hubspot_deal_information',
          label: 'HubSpot Deal Information',
          description:
            'Include HubSpot deal information for richer context during conversations',
          enabled: false,
          defaultEnabled: false
        },
        {
          key: 'guesty_reservation_information',
          label: 'Guesty Reservation Information',
          description:
            'Include reservation details, lockbox codes, and WiFi passwords from Guesty',
          enabled: false,
          defaultEnabled: false
        }
      ]
    }
  },

  computed: {
    ...mapState('cache', ['current_company']),

    contextsWithConfigs() {
      return this.availableContexts.filter((context) => {
        return Array.isArray(context.config) && context.config.length > 0
      })
    },

    contextsWithoutConfigs() {
      return this.availableContexts.filter((context) => {
        return !Array.isArray(context.config) || context.config.length === 0
      })
    }
  },

  mounted() {
    this.initializeContexts()
  },

  methods: {
    initializeContexts() {
      // Reset all contexts to disabled first
      this.availableContexts.forEach((context) => {
        context.enabled = false
        if (Array.isArray(context.config)) {
          context.config.forEach((configItem) => {
            configItem.value = configItem.default
          })
        }
      })

      // Initialize contexts from bot data if available
      if (this.bot.contexts && typeof this.bot.contexts === 'object') {
        Object.keys(this.bot.contexts).forEach((contextKey) => {
          const context = this.availableContexts.find(
            (c) => c.key === contextKey
          )
          if (context) {
            context.enabled = true

            // Set config values if provided
            const contextConfig = this.bot.contexts[contextKey]
            if (
              contextConfig &&
              typeof contextConfig === 'object' &&
              Array.isArray(context.config)
            ) {
              context.config.forEach((configItem) => {
                if (contextConfig[configItem.key] !== undefined) {
                  configItem.value = contextConfig[configItem.key]
                }
              })
            }
          }
        })
      } else {
        // If no contexts are defined, enable contexts based on their defaultEnabled flag
        this.availableContexts.forEach((context) => {
          if (Object.prototype.hasOwnProperty.call(context, 'defaultEnabled')) {
            context.enabled = !!context.defaultEnabled
          }
        })

        // Update the bot with default contexts
        this.updateBotContexts()
      }
    },

    handleContextToggle(context) {
      this.updateBotContexts()
    },

    handleConfigChange(context, config) {
      this.updateBotContexts()
    },

    handleSelectChange(context, config, value) {
      this.$set(config, 'value', value)
      this.handleConfigChange(context, config)
    },

    updateBotContexts() {
      // Create contexts object for the bot using the same keys
      const contexts = {}

      this.availableContexts.forEach((context) => {
        // Only include contexts that are available and enabled
        if (!this.isContextAvailable(context) || !context.enabled) {
          return
        }

        if (Array.isArray(context.config)) {
          // Context has configurations
          const configs = {}
          context.config.forEach((configItem) => {
            // Include config even if value is empty (will use all available fields)
            configs[configItem.key] = configItem.value || []
          })
          contexts[context.key] = configs
        } else {
          // Context without configurations - store as array
          contexts[context.key] = []
        }
      })

      this.$set(this.bot, 'contexts', contexts)
    },

    isContextAvailable(context) {
      // Check if context is available based on its key
      if (context.key === 'hubspot_deal_information') {
        return !!(
          this.current_company &&
          this.current_company.hubspot_integration_enabled
        )
      }

      if (context.key === 'guesty_reservation_information') {
        if (
          !this.current_company ||
          !this.current_company.guesty_integration_enabled
        ) {
          return false
        }

        // TODO: Remove company ID restriction once Guesty Lock Codes API is fully validated
        if (appInProduction()) {
          const allowedCompanyIds = [7, 47, 1659, 2565, 9248]
          return allowedCompanyIds.includes(this.current_company.id)
        }

        return true
      }

      // All other contexts are always available
      return true
    }
  },

  watch: {
    bot: {
      handler() {
        this.initializeContexts()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.context-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.context-item {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 1.5rem;
  background-color: #fff;
  transition: all 0.3s ease;
}

.context-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.context-item-full {
  width: 100%;
}

.context-item-simple {
  padding: 1rem 1.5rem;
}

.context-header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 1rem;
}

.context-toggle {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-right: 1rem;
}

.context-info {
  flex: 1;
}

.context-name {
  margin: 0 0 0.5rem 0;
  font-weight: 600;
  color: #303133;
  font-size: 1.1rem;
}

.context-description {
  margin: 0;
  color: #606266;
  font-size: 0.9rem;
  line-height: 1.4;
}

.context-config {
  border-top: 1px solid #f0f0f0;
  padding-top: 1rem;
}

.config-field {
  margin-bottom: 1rem;
}

.config-field:last-child {
  margin-bottom: 0;
}

.form-label h6 {
  margin: 0 0 0.25rem 0;
  font-size: 0.95rem;
  font-weight: 600;
}

.form-label small {
  color: #909399;
  font-size: 0.85rem;
}
</style>
